import { useEffect, useRef } from 'react';
import intlTelInput from 'intl-tel-input/intlTelInputWithUtils';
import 'intl-tel-input/styles';
import { cn } from '@/lib/utils';

// PhoneInput — React wrapper around intl-tel-input. The library is
// imperative and DOM-driven, so we mount it once per <input> ref and
// keep React's controlled value in sync via setNumber() / a manual
// 'input' + 'countrychange' listener.
//
// The config mirrors what frontend/src/sites/runtime/main.ts uses on the
// public-website signup forms: Nordic-first country order, dial code
// rendered outside the input so the user only types the national
// significant number, strict-mode pasting, and E.164 out via getNumber()
// once the number parses cleanly.
//
// Emitted value: full E.164 form ("+46701234567") once the user has typed
// a complete valid number, OR the raw text as-typed while they're still
// editing. That way the parent's local state is always a string the user
// recognises, and on submit you can do `isValidPhoneE164(value)` (the
// "+" prefix is a sufficient marker) before sending to the API.

// `as const` so TS keeps the literal-union shape that intl-tel-input's
// `countryOrder` prop demands instead of widening to `string[]`.
const NORDIC_FIRST = ['se', 'no', 'dk', 'fi', 'is', 'gb', 'de', 'us'] as const;

export interface PhoneInputProps {
  value: string;
  onChange: (next: string) => void;
  defaultCountry?: string;        // ISO-3166 alpha-2, lowercase. Default 'se'.
  placeholder?: string;
  className?: string;
  id?: string;
  'aria-label'?: string;
  required?: boolean;
  // Fires with the live libphonenumber verdict whenever the value changes
  // (and on external prefill). Empty input reports `true` (a blank optional
  // phone isn't "invalid"). Lets the parent show a non-blocking warning that
  // matches the backend's nyaruka/phonenumbers check exactly.
  onValidityChange?: (valid: boolean) => void;
}

export function PhoneInput({
  value,
  onChange,
  defaultCountry = 'se',
  placeholder,
  className,
  id,
  'aria-label': ariaLabel,
  required,
  onValidityChange,
}: PhoneInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const itiRef = useRef<ReturnType<typeof intlTelInput> | null>(null);
  // Keep the latest onValidityChange in a ref so the init effect (which only
  // re-runs on defaultCountry change) always calls the current callback
  // instead of a stale closure.
  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;
  // Mirror of the last value we pushed into the DOM via setNumber(). Lets
  // us avoid a feedback loop where our own onChange re-sets the same
  // value and the input snaps to the start of the line on each keystroke.
  const lastSyncedRef = useRef<string>('');

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    // initialCountry's TS literal union covers every ISO code; cast since
    // the runtime value is a real ISO code the library accepts.
    const iti = intlTelInput(el, {
      initialCountry: defaultCountry.toLowerCase() as 'se',
      // intl-tel-input types countryOrder as a mutable literal-union array.
      // We keep the constant readonly to prevent accidental mutation; copy
      // to a plain array at the call site to satisfy the lib's signature.
      countryOrder: [...NORDIC_FIRST],
      separateDialCode: true,
      strictMode: true,
      nationalMode: false,
    });
    itiRef.current = iti;

    function handle() {
      const raw = el?.value.trim() ?? '';
      // When the field is empty, emit empty so the form sees a falsy
      // value (the API treats '' as "no phone"). A blank optional phone is
      // not "invalid", so report valid=true.
      if (raw === '') {
        lastSyncedRef.current = '';
        onChange('');
        onValidityChangeRef.current?.(true);
        return;
      }
      // Once the number parses, emit canonical E.164. Otherwise emit
      // the user's typing so the parent can still gate "is this empty?"
      // checks but won't try to send a half-typed number to the server.
      // isValidNumber() returns boolean | null (null = utils not yet loaded);
      // treat anything non-true as "not valid yet".
      const valid = iti.isValidNumber() === true;
      const next = valid ? iti.getNumber() : raw;
      lastSyncedRef.current = next;
      onChange(next);
      onValidityChangeRef.current?.(valid);
    }

    el.addEventListener('input', handle);
    el.addEventListener('countrychange', handle);
    return () => {
      el.removeEventListener('input', handle);
      el.removeEventListener('countrychange', handle);
      iti.destroy();
      itiRef.current = null;
    };
    // We deliberately do NOT depend on `value` here — the iti instance
    // owns the DOM input's `value` after init, and React shouldn't
    // re-mount on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountry]);

  // Apply external value changes (e.g. profile prefill from getMe). Only
  // call setNumber() when the value genuinely differs from what we last
  // emitted, otherwise we'd nuke the caret on every parent re-render.
  useEffect(() => {
    const iti = itiRef.current;
    if (!iti) return;
    if (value === lastSyncedRef.current) return;
    iti.setNumber(value);
    lastSyncedRef.current = value;
    // Report validity for the prefilled value so the parent's warning reflects
    // legacy/imported data the moment the card opens, not just after an edit.
    onValidityChangeRef.current?.(value.trim() === '' ? true : iti.isValidNumber() === true);
  }, [value]);

  return (
    <input
      ref={inputRef}
      type="tel"
      autoComplete="tel"
      inputMode="tel"
      placeholder={placeholder}
      id={id}
      aria-label={ariaLabel}
      required={required}
      className={cn(
        // Match the design-system Input dimensions / pill border so the
        // text field next to the flag picker looks like the rest of the
        // form. iti's own .iti wrapper adds the flag selector to the left.
        'min-h-[var(--tap)] w-full rounded-pill border border-border bg-background px-4 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    />
  );
}
